import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Target, DollarSign, Users, HandHeart, Star } from "lucide-react";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { Fundraiser } from "../../../models/FundraisersGridModels";
import { CreateFundraiserModel } from "../../../models";

const ProgressContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ProgressTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressWrapper = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 32px;
  border: 1px solid rgba(255, 255, 255, 0.5);
`;

const ProgressContent = styled.div`
  margin-bottom: 32px;
`;

const ProgressLabelsTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const AmountCaptionTop = styled.div`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
`;

const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const ProgressBarFill = styled.div<{ percentage: number }>`
  width: ${(props) => props.percentage}%;
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  border-radius: 6px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const ProgressLabelsBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AmountValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }
`;

const StatIcon = styled.div<{ color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px ${(props) => props.color}40;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
`;

interface ProgressSectionProps {
  fundraiser: CreateFundraiserModel;
}

export const ProgressSection: React.FC<ProgressSectionProps> = observer(
  ({ fundraiser }) => {
    const [displayedPercentage, setDisplayedPercentage] = useState(0);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    // Get donations for this fundraiser
    const donations = fundraiserStore.getFundraiserDonations(fundraiser.id);

    // Calculate unique donors for this fundraiser
    const uniqueDonors = new Set(
      donations.map((d) => d.donorEmail || d.donorName)
    ).size;

    // Calculate fundraiser rank by comparing total collected amounts
    // const getAllFundraisersRank = () => {
    //   const allFundraisers = fundraiserStore.fundraisers;
    //   const sortedByAmount = allFundraisers
    //     .slice()
    //     .sort((a, b) => b.currentAmount - a.currentAmount);
    //   const rank = sortedByAmount.findIndex((f) => f.id === fundraiser.id) + 1;
    //   return rank;
    // };

    // const fundraiserRank = getAllFundraisersRank();
    const progress = (fundraiser.totalRaisedAmount / fundraiser.goal) * 100;

    // For display purposes, always show actual amounts and percentages
    const displayProgress = progress;
    // For bar fill, cap at 100%
    const barProgress = Math.min(progress, 100);

    useEffect(() => {
      const timer = setTimeout(() => {
        setDisplayedPercentage(barProgress);
      }, 300);

      return () => clearTimeout(timer);
    }, [barProgress]);

    return (
      <ProgressContainer>
        <ProgressTitle>
          <Target size={28} color="#3b82f6" />
          Progress
        </ProgressTitle>
        <ProgressWrapper>
          <ProgressContent>
            {fundraiser.goal > 0 ? (
              <ProgressLabelsTop>
                <AmountCaptionTop>Raised</AmountCaptionTop>
                <AmountCaptionTop>Goal</AmountCaptionTop>
              </ProgressLabelsTop>
            ) : (
              <ProgressLabelsTop>
                <div></div>
                <AmountCaptionTop>Raised</AmountCaptionTop>
              </ProgressLabelsTop>
            )}

            <ProgressBarContainer>
              <ProgressBarFill percentage={displayedPercentage} />
            </ProgressBarContainer>

            {fundraiser.goal > 0 ? (
              <ProgressLabelsBottom>
                <AmountValue>
                  {formatCurrency(fundraiser.totalRaisedAmount)}
                </AmountValue>
                <AmountValue>{formatCurrency(fundraiser.goal)}</AmountValue>
              </ProgressLabelsBottom>
            ) : (
              <ProgressLabelsBottom>
                <div></div>
                <AmountValue>
                  {formatCurrency(fundraiser.totalRaisedAmount)}
                </AmountValue>
              </ProgressLabelsBottom>
            )}
          </ProgressContent>

          <StatsGrid>
            <StatCard>
              <StatIcon color="#10b981">
                <DollarSign size={28} />
              </StatIcon>
              <StatValue>{fundraiser.totalDonations}</StatValue>
              <StatLabel>Total Donations</StatLabel>
            </StatCard>

            <StatCard>
              <StatIcon color="#3b82f6">
                <HandHeart size={28} />
              </StatIcon>
              <StatValue>{fundraiser.totalDonors}</StatValue>
              <StatLabel>Total Donors</StatLabel>
            </StatCard>

            <StatCard>
              <StatIcon color="#f59e0b">
                <Users size={28} />
              </StatIcon>
              <StatValue>{fundraiser.activePromoters}</StatValue>
              <StatLabel>Active Promoters</StatLabel>
            </StatCard>

            <StatCard>
              <StatIcon color="#8b5cf6">
                <Star size={28} />
              </StatIcon>
              <StatValue>{fundraiser.rank}</StatValue>
              <StatLabel>Fundraiser Rank</StatLabel>
            </StatCard>
          </StatsGrid>
        </ProgressWrapper>
      </ProgressContainer>
    );
  }
);
